const hasUrl = (r) => { var _a; return Boolean((_a = r.url) === null || _a === void 0 ? void 0 : _a.trim()); };
export function downloadSource(asset) {
    var _a, _b;
    if (asset.type !== "movie" && asset.type !== "episode") {
        return { refusal: { code: "not_playable_type", type: asset.type } };
    }
    const mains = ((_a = asset.medias) !== null && _a !== void 0 ? _a : [])
        .filter((m) => m.type === "main")
        .sort((a, b) => {
        var _a, _b, _c, _d;
        return ((_a = b.videoWidth) !== null && _a !== void 0 ? _a : 0) * ((_b = b.videoHeight) !== null && _b !== void 0 ? _b : 0) -
            ((_c = a.videoWidth) !== null && _c !== void 0 ? _c : 0) * ((_d = a.videoHeight) !== null && _d !== void 0 ? _d : 0);
    });
    if (mains.length === 0)
        return { refusal: { code: "no_main_media" } };
    const all = mains.flatMap((m) => { var _a; return (_a = m.mediaResources) !== null && _a !== void 0 ? _a : []; }).filter(hasUrl);
    const skippedEncrypted = all.some((r) => r.isEncrypted);
    for (const media of mains) {
        const hls = ((_b = media.mediaResources) !== null && _b !== void 0 ? _b : []).find((r) => hasUrl(r) && !r.isEncrypted && String(r.protocol) === "hls");
        if (hls) {
            return {
                source: {
                    mediaId: media.mediaId,
                    manifestUrl: hls.url.trim(),
                    skippedEncrypted,
                },
            };
        }
    }
    const clear = all.filter((r) => !r.isEncrypted);
    if (clear.some((r) => String(r.protocol) === "dash")) {
        return { refusal: { code: "dash_only" } };
    }
    if (clear.length === 0 && skippedEncrypted) {
        return { refusal: { code: "encrypted_only" } };
    }
    return { refusal: { code: "no_stream" } };
}
export function downloadRefusalText(refusal) {
    switch (refusal.code) {
        case "not_playable_type":
            return `A ${refusal.type} has no video of its own - download its episodes instead.`;
        case "no_main_media":
            return "This title has no main media file to download.";
        case "encrypted_only":
            return "This title is only packaged encrypted, and an encrypted file cannot be watched outside a licensed player.";
        case "dash_only":
            return "This title's unencrypted copy is DASH only. Download reads HLS manifests.";
        case "no_stream":
            return "This title has no stream URL to download from.";
    }
}
export class HlsParseError extends Error {
}
export function parseAttributes(list) {
    const out = {};
    const re = /([A-Z0-9-]+)=("[^"]*"|[^,]*)/g;
    let m;
    while ((m = re.exec(list)) !== null) {
        const raw = m[2];
        out[m[1]] =
            raw.startsWith('"') && raw.endsWith('"') ? raw.slice(1, -1) : raw;
    }
    return out;
}
function absolute(uri, base) {
    return new URL(uri, base).toString();
}
function positiveInt(value) {
    if (value === undefined)
        return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}
export function parseHlsPlaylist(text, baseUrl) {
    var _a, _b, _c, _d, _e, _f;
    const lines = (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)
        .split(/\r?\n/)
        .map((l) => l.trim());
    if (lines[0] !== "#EXTM3U") {
        throw new HlsParseError("Not an HLS playlist: it does not start with #EXTM3U.");
    }
    const variants = [];
    const audio = [];
    let pending = null;
    let sawSegments = false;
    for (const line of lines.slice(1)) {
        if (!line)
            continue;
        if (line.startsWith("#EXT-X-STREAM-INF:")) {
            pending = parseAttributes(line.slice("#EXT-X-STREAM-INF:".length));
            continue;
        }
        if (line.startsWith("#EXT-X-MEDIA:")) {
            const a = parseAttributes(line.slice("#EXT-X-MEDIA:".length));
            if (a.TYPE === "AUDIO" && a["GROUP-ID"]) {
                audio.push({
                    groupId: a["GROUP-ID"],
                    name: (_a = a.NAME) !== null && _a !== void 0 ? _a : null,
                    language: (_b = a.LANGUAGE) !== null && _b !== void 0 ? _b : null,
                    isDefault: a.DEFAULT === "YES",
                    uri: a.URI ? absolute(a.URI, baseUrl) : null,
                });
            }
            continue;
        }
        if (line.startsWith("#EXTINF"))
            sawSegments = true;
        if (line.startsWith("#"))
            continue;
        if (pending) {
            const resolution = /^(\d+)x(\d+)$/.exec((_c = pending.RESOLUTION) !== null && _c !== void 0 ? _c : "");
            variants.push({
                uri: absolute(line, baseUrl),
                bandwidth: (_d = positiveInt(pending.BANDWIDTH)) !== null && _d !== void 0 ? _d : 0,
                averageBandwidth: positiveInt(pending["AVERAGE-BANDWIDTH"]),
                width: resolution ? Number(resolution[1]) : null,
                height: resolution ? Number(resolution[2]) : null,
                codecs: (_e = pending.CODECS) !== null && _e !== void 0 ? _e : null,
                audioGroup: (_f = pending.AUDIO) !== null && _f !== void 0 ? _f : null,
            });
            pending = null;
        }
    }
    if (variants.length > 0)
        return { kind: "master", variants, audio };
    if (sawSegments)
        return { kind: "media" };
    throw new HlsParseError("The playlist names no variants and no segments.");
}
const VIDEO_CODEC = /^(avc|hvc|hev|dvh|dva|av01|vp0?9|mp4v)/i;
function isAudioOnly(v) {
    if (v.width !== null)
        return false;
    if (!v.codecs)
        return false;
    return !v.codecs.split(",").some((c) => VIDEO_CODEC.test(c.trim()));
}
export function pickTopRung(master) {
    const candidates = master.variants.filter((v) => !isAudioOnly(v));
    if (candidates.length === 0) {
        throw new HlsParseError("The master playlist has no video variant.");
    }
    const pixels = (v) => { var _a, _b; return ((_a = v.width) !== null && _a !== void 0 ? _a : 0) * ((_b = v.height) !== null && _b !== void 0 ? _b : 0); };
    const video = [...candidates].sort((a, b) => {
        var _a, _b;
        return pixels(b) - pixels(a) ||
            b.bandwidth - a.bandwidth ||
            ((_a = b.averageBandwidth) !== null && _a !== void 0 ? _a : 0) - ((_b = a.averageBandwidth) !== null && _b !== void 0 ? _b : 0);
    })[0];
    const group = video.audioGroup
        ? master.audio.filter((a) => a.groupId === video.audioGroup)
        : [];
    const seen = new Set();
    const audio = [...group]
        .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
        .filter((a) => {
        if (!a.uri || seen.has(a.uri))
            return false;
        seen.add(a.uri);
        return true;
    });
    const muxedAudio = group.length === 0 || group.some((a) => !a.uri);
    return { video, audio, muxedAudio };
}
export function downloadFileName(title, rung) {
    const suffix = (rung === null || rung === void 0 ? void 0 : rung.height) ? `-${rung.height}p` : "";
    const base = (title || "title")
        .trim()
        .replace(/[\\/:*?"<>|\p{Cc}]+/gu, " ")
        .replace(/\s+/g, " ")
        .trim() || "title";
    const ascii = base
        .normalize("NFKD")
        .replace(/\p{M}+/gu, "")
        .replace(/[^A-Za-z0-9 ._-]+/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^[-.]+|[-.]+$/g, "") || "title";
    return { ascii: `${ascii}${suffix}.mp4`, utf8: `${base}${suffix}.mp4` };
}
